import React from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  MapPinIcon,
  SearchIcon,
  GlobeIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "./ui/dropdown-menu";
import { mockData } from "../data/mock";
import BookAppointmentModal from "./BookAppointmentModal";

const Header = () => {
  const navigate = useNavigate();

  const handleTreatmentClick = (specialtyName, treatmentName) => {
    if (specialtyName.toLowerCase() === "ophthalmology") {
      // Convert treatment name to URL format
      const treatmentUrl = treatmentName
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[()]/g, "");
      navigate(`/ophthalmology/${treatmentUrl}`);
    }
  };

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img
              className="h-6 w-6 sm:h-8 sm:w-8"
              src="/image/img_icon.png"
              alt="icon"
            />
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">
              TheLivCure
            </h1>
          </Link>

          {/* Search & Location */}
          <div className="hidden md:flex items-center space-x-4 flex-1 max-w-2xl mx-4 lg:mx-8">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search disease, doctors, treatment..."
                className="pl-10 pr-4 w-full text-sm"
              />
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
            {/* Mobile Menu Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onSelect={() => navigate("/find-doctors")}>
                  Find Doctors
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/ophthalmology")}>
                  Eye Treatments
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/about")}>
                  About Us
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/hospitals")}>
                  Our Hospitals
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/contact")}>
                  Contact Us
                </DropdownMenuItem>
                {false && (
                  <DropdownMenuItem onSelect={() => navigate("/blog")}>
                    Health Blog
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs sm:text-sm hidden md:flex"
                >
                  For Patients
                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => navigate("/find-doctors")}>
                  Find Doctors
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/ophthalmology")}>
                  Eye Treatments
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <BookAppointmentModal>
                    <span className="w-full text-left">Book Appointment</span>
                  </BookAppointmentModal>
                </DropdownMenuItem>
                {false && (
                  <DropdownMenuItem onSelect={() => navigate("/blog")}>
                    Health Blog
                  </DropdownMenuItem>
                )}
                {false && (
                  <DropdownMenuItem
                    onSelect={() => navigate("/patient-stories")}
                  >
                    Patient Stories
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs sm:text-sm hidden lg:flex"
                >
                  Our Company
                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => navigate("/about")}>
                  About Us
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/hospitals")}>
                  Our Hospitals
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/contact")}>
                  Contact Us
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <BookAppointmentModal>
              <Button className="bg-gray-900 hover:bg-gray-600 text-white text-xs sm:text-sm px-2 sm:px-4 min-h-[40px]">
                <span className="hidden md:inline">Book Free Appointment</span>
                <span className="md:hidden">Book</span>
              </Button>
            </BookAppointmentModal>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="hidden lg:flex">
                  <GlobeIcon className="w-4 h-4" />
                  English
                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>English</DropdownMenuItem>
                <DropdownMenuItem>हिंदी</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="hidden xl:flex">
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  Select City
                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>Delhi</DropdownMenuItem>
                <DropdownMenuItem>Gurgaon</DropdownMenuItem>
                <DropdownMenuItem>Noida</DropdownMenuItem>
                <DropdownMenuItem>Faridabad</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="md:hidden bg-white border-t border-gray-100 px-4 py-3">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search disease, doctors, treatment..."
            className="pl-10 pr-4 w-full text-sm"
          />
        </div>
      </div>

      {/* Specialties Navigation */}
      <div className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="scroll-fade-right">
            <nav className="flex items-center space-x-3 sm:space-x-6 py-2 sm:py-3 overflow-x-auto specialties-scrollbar">
              {/* Ophthalmology Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="whitespace-nowrap font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 sm:px-4 py-1.5 sm:py-2 flex-shrink-0 rounded-full transition-all duration-200 hover:shadow-sm text-sm sm:text-base"
                  >
                    Ophthalmology
                    <ChevronDownIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuItem
                    onSelect={() => navigate("/ophthalmology")}
                    className="font-semibold text-blue-600"
                  >
                    View All Treatments
                  </DropdownMenuItem>
                  <div className="border-t border-gray-100 my-1"></div>

                  {/* LASIK Submenu */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="text-sm">
                      LASIK Treatments
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "Femto LASIK Surgery"
                          )
                        }
                        className="text-sm"
                      >
                        Femto LASIK
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "Contoura Vision"
                          )
                        }
                        className="text-sm"
                      >
                        Contoura Vision
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "SMILE LASIK Surgery"
                          )
                        }
                        className="text-sm"
                      >
                        SMILE
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "PRK LASIK Surgery"
                          )
                        }
                        className="text-sm"
                      >
                        PRK LASIK
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  {/* Retinopathy Submenu */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="text-sm">
                      Retina Treatment
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "Diabetic Retinopathy"
                          )
                        }
                        className="text-sm"
                      >
                        Diabetic Retinopathy
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "Retinal Detachment"
                          )
                        }
                        className="text-sm"
                      >
                        Retinal Detachment
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "Vitrectomy Surgery"
                          )
                        }
                        className="text-sm"
                      >
                        Vitrectomy Surgery
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  {/* Cataract Submenu */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="text-sm">
                      Cataract Treatments
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick(
                            "Ophthalmology",
                            "Cataract Surgery"
                          )
                        }
                        className="text-sm"
                      >
                        Cataract Surgery
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          handleTreatmentClick("Ophthalmology", "Phaco Surgery")
                        }
                        className="text-sm"
                      >
                        Phaco Surgery
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  {/* Other Treatments */}
                  {mockData.specialties[0]?.treatments
                    ?.filter(
                      (treatment) =>
                        !treatment.includes("LASIK") &&
                        treatment !== "Diabetic Retinopathy" &&
                        treatment !== "Retinal Detachment" &&
                        treatment !== "Vitrectomy Surgery" &&
                        treatment !== "Contoura Vision" &&
                        treatment !== "Cataract Surgery" &&
                        treatment !== "Phaco Surgery"
                    )
                    .map((treatment) => (
                      <DropdownMenuItem
                        key={treatment}
                        onSelect={() =>
                          handleTreatmentClick("Ophthalmology", treatment)
                        }
                        className="text-sm"
                      >
                        {treatment}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Separator */}
              <div className="h-3 sm:h-4 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent flex-shrink-0"></div>

              {/* Individual Treatment Buttons */}
              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 px-2 sm:px-3 py-1 sm:py-1.5 h-auto flex-shrink-0 rounded-full transition-all duration-200 hover:shadow-sm"
                onClick={() => navigate("/gall-stone")}
              >
                Gall Stone
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 px-2 sm:px-3 py-1 sm:py-1.5 h-auto flex-shrink-0 rounded-full transition-all duration-200 hover:shadow-sm"
                onClick={() => navigate("/kidney-stone")}
              >
                Kidney Stone
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 px-2 sm:px-3 py-1 sm:py-1.5 h-auto flex-shrink-0 rounded-full transition-all duration-200 hover:shadow-sm"
                onClick={() => navigate("/piles")}
              >
                Piles
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 px-2 sm:px-3 py-1 sm:py-1.5 h-auto flex-shrink-0 rounded-full transition-all duration-200 hover:shadow-sm"
                onClick={() => navigate("/circumcision")}
              >
                Circumcision
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 px-2 sm:px-3 py-1 sm:py-1.5 h-auto flex-shrink-0 rounded-full transition-all duration-200 hover:shadow-sm"
                onClick={() => navigate("/hernia")}
              >
                Hernia
              </Button>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
